import React, { useState, useEffect } from "react";
import { FileText, Loader2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { getIntakeForm } from "../lib/therapistApi";
import { toast } from "sonner";

const ClientIntakeFormView = ({ bookingId, clientEmail, clientId, open, onOpenChange }) => {
  const [intakeForm, setIntakeForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && (bookingId || clientEmail || clientId)) {
      loadIntakeForm();
    } else if (!open) {
      // Reset state when dialog closes
      setIntakeForm(null);
      setError(null);
    }
  }, [open, bookingId, clientEmail, clientId]);

  const loadIntakeForm = async () => {
    try {
      setLoading(true);
      setError(null);
      // Prioritize bookingId - each booking has its own intake form
      const params = {};
      if (bookingId) {
        params.bookingId = bookingId;
      } else if (clientEmail) {
        params.email = clientEmail;
      } else if (clientId) {
        params.clientId = clientId;
      }

      const data = await getIntakeForm(params);
      setIntakeForm(data.intakeForm);
    } catch (err) {
      console.error("Error loading intake form:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to load intake form";
      setError(errorMessage);
      // Only show toast if it's not a "not found" error (404)
      if (err?.response?.status !== 404) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPressureLevelBadge = (level) => {
    const variants = {
      Light: "secondary",
      Medium: "default",
      Firm: "destructive",
    };
    return (
      <Badge variant={variants[level] || "secondary"} className="ml-2">
        {level}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            Client Intake Form
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            {error.includes("not found") ? (
              <>
                <AlertCircle className="h-8 w-8 text-amber-500" />
                <p className="text-sm text-gray-600 text-center">
                  No intake form has been submitted for this client yet.
                </p>
              </>
            ) : (
              <>
                <AlertCircle className="h-8 w-8 text-red-500" />
                <p className="text-sm text-red-600 text-center">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadIntakeForm}
                  className="gap-2">
                  <Loader2 className="h-4 w-4" />
                  Retry
                </Button>
              </>
            )}
          </div>
        ) : intakeForm ? (
          <div className="space-y-4">
            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {intakeForm.clientName && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Name:</span>
                    <span className="ml-2 text-sm text-gray-900">{intakeForm.clientName}</span>
                  </div>
                )}
                {intakeForm.clientEmail && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Email:</span>
                    <span className="ml-2 text-sm text-gray-900">{intakeForm.clientEmail}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pressure Level */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Massage Preferences</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-600">Preferred Pressure Level:</span>
                  {getPressureLevelBadge(intakeForm.pressureLevel)}
                </div>
              </CardContent>
            </Card>

            {/* What brings you in today */}
            {intakeForm.whatBringsYouInToday && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What Brings You In Today</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">
                    {intakeForm.whatBringsYouInToday}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Health Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Health Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Broken Skin:</span>
                  <div className="mt-1 flex items-center gap-2">
                    {intakeForm.brokenSkin ? (
                      <>
                        <Badge variant="destructive">Yes</Badge>
                        {intakeForm.brokenSkinDescription && (
                          <p className="text-sm text-gray-700 ml-2">
                            {intakeForm.brokenSkinDescription}
                          </p>
                        )}
                      </>
                    ) : (
                      <Badge variant="secondary">No</Badge>
                    )}
                  </div>
                </div>

                {intakeForm.medicationInfo && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 block mb-1">
                      Current Medications:
                    </span>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                      {intakeForm.medicationInfo}
                    </p>
                  </div>
                )}

                {intakeForm.additionalNotes && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 block mb-1">
                      Additional Notes:
                    </span>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                      {intakeForm.additionalNotes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Metadata */}
            <div className="text-xs text-gray-500 pt-2 border-t">
              {intakeForm.createdAt && (
                <p>
                  Submitted: {new Date(intakeForm.createdAt).toLocaleString()}
                </p>
              )}
              {intakeForm.updatedAt && intakeForm.updatedAt !== intakeForm.createdAt && (
                <p>
                  Last updated: {new Date(intakeForm.updatedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ) : null}

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientIntakeFormView;

